import { useEffect, useState } from 'react'
import type { ContratoParcela } from '@services/db.js'
import type { PagamentoParcelaInput } from '@services/gestao.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { MoneyInput } from '@components/primitives/MoneyInput.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { Alert } from '@components/primitives/Alert.js'
import { FORMAS_PAGAMENTO } from '@utils/constants.js'
import { formatCurrency } from '@utils/format.js'

interface Props {
  open: boolean
  parcela: ContratoParcela | null
  prestador: string
  onClose: () => void
  onPagar: (id: number, input: PagamentoParcelaInput) => Promise<void>
}

const hoje = () => new Date().toISOString().slice(0, 10)

const FORMAS = FORMAS_PAGAMENTO
const FORMA_PADRAO = FORMAS.find((f) => f.value === 'pix')?.value ?? FORMAS[0]?.value ?? 'pix'

export function PagarParcelaModal({ open, parcela, prestador, onClose, onPagar }: Props) {
  const [valor, setValor] = useState(0)
  const [data, setData] = useState(hoje())
  const [forma, setForma] = useState<string>(FORMA_PADRAO)
  const [obs, setObs] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    if (!open || !parcela) return
    setValor(parcela.valor)
    setData(hoje())
    setForma(FORMA_PADRAO)
    setObs('')
    setCopiado(false)
  }, [open, parcela])

  if (!parcela) return null

  const naoLiberada = parcela.status === 'prevista' && parcela.condicao !== 'sinal'
  const diferente = valor > 0 && valor !== parcela.valor

  async function copiarValor() {
    const texto = valor.toFixed(2).replace('.', ',')
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      window.setTimeout(() => setCopiado(false), 2000)
    } catch {
      setCopiado(false)
    }
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!parcela || valor <= 0) return
    setSalvando(true)
    await onPagar(parcela.id!, {
      dataPagamento: data,
      formaPagamento: forma,
      ...(diferente ? { valor } : {}),
      ...(obs.trim() ? { observacoes: obs.trim() } : {}),
    })
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title='Registrar pagamento'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='pagar-form'
            loading={salvando}
            disabled={valor <= 0}
          >
            Confirmar pagamento
          </Button>
        </div>
      }
    >
      <form id='pagar-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        <div className='cf3-block__card cf3-block__card--hint cf3-pagar-resumo'>
          <span className='cf3-pagar-resumo__desc'>{parcela.descricao}</span>
          <span className='cf3-pagar-resumo__meta'>
            {prestador} · previsto {formatCurrency(parcela.valor)}
          </span>
        </div>

        {naoLiberada && (
          <Alert
            variant='warning'
            title='Serviço ainda não medido'
            message='Esta parcela não foi liberada. Confira o serviço no local antes de pagar. Pagar antes do medido é a principal causa de prejuízo do dono da obra.'
          />
        )}

        <div className='cf3-gestao-form__row'>
          <MoneyInput label='Valor pago' value={valor} required onChange={setValor} />
          <Input
            label='Data do pagamento'
            type='date'
            value={data}
            required
            onChange={(e) => setData(e.target.value)}
          />
        </div>

        <div className='cf-field'>
          <span className='cf-field__label'>Forma de pagamento</span>
          <div className='cf3-chip-row' role='radiogroup' aria-label='Forma de pagamento'>
            {FORMAS.map((f) => (
              <button
                key={f.value}
                type='button'
                role='radio'
                aria-checked={forma === f.value}
                className={`cf3-chip${forma === f.value ? ' cf3-chip--active' : ''}`}
                onClick={() => setForma(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {forma === 'pix' && valor > 0 && (
          <button
            type='button'
            className='cf3-gestao-form__link'
            onClick={() => void copiarValor()}
          >
            {copiado ? 'Valor copiado' : `Copiar ${formatCurrency(valor)} para o PIX`}
          </button>
        )}

        <Textarea
          label='Observação (opcional)'
          value={obs}
          rows={2}
          placeholder='Ex.: pago via Pix para o CPF do contrato'
          onChange={(e) => setObs(e.target.value)}
        />

        <p className='cf3-gestao-form__nota'>
          Cria um lançamento em Financeiro › Mão de Obra, já marcado como pago.
        </p>
      </form>
    </Modal>
  )
}
